import { faArrowAltCircleRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useContext, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Alert from 'src/components/alert';
import Header from 'src/components/header';
import Loader from 'src/components/loader';
import NotFound from 'src/components/not-found';
import ScanButton from 'src/components/scan-button';
import ViewRunButton from 'src/components/view-run-button';
import FeaturesContext from 'src/context/FeaturesContext';
import PrintDetails from 'src/pages/piece-overview/[uuid]/sections/_print-details';
import { api } from 'src/utils/api';
import { API_RESOURCES, LINE_ITEM_COMPOSITION_TYPES } from 'src/utils/constants';
import { FEATURES, isFeatureEnabled } from 'src/utils/features';
import routes from 'src/utils/routes';
import { getRouteURI, getUuid } from 'src/utils/url';
import userPropType from 'src/utils/user-prop-type';

import LineItem from './sections/_line-item';
import ModelViewerModal from './sections/_model-viewer-modal';
import Order from './sections/_order';
import PieceHeader from './sections/_piece-header';
import Thumbnail from './sections/_thumbnail';
import Workflow from './sections/_workflow';

const PieceOverview = ({ user }) => {
  const { uuid: pieceUUID } = useParams();
  const [piece, setPiece] = useState(undefined);
  const [model, setModel] = useState(undefined);
  const [run, setRun] = useState(undefined);
  const [assemblyRootLineItem, setAssemblyRootLineItem] = useState(null);
  const [runUUID, setRunUUID] = useState(undefined);
  const [error, setError] = useState(undefined);
  const [thumbnail, setThumbnail] = useState(null);
  const [showModelViewerModal, setShowModelViewerModal] = useState(false);
  const [noModelPiece, setNoModelPiece] = useState(false);

  const { features } = useContext(FeaturesContext);
  const isPowderWorkflow = isFeatureEnabled(features, FEATURES.POWDER_WORKFLOW);

  const fetchInitialData = async () => {
    try {
      const piece = await api.get(`${API_RESOURCES.PIECE}/${pieceUUID}/`).json();
      const isAssembly = piece.composition_type === LINE_ITEM_COMPOSITION_TYPES.ASSEMBLY;
      const isAssemblyPart = piece.composition_type === LINE_ITEM_COMPOSITION_TYPES.ASSEMBLY_PART;
      setPiece(piece);

      if ((isAssemblyPart || isAssembly) && piece?.order) {
        const { resources: assemblyParts } = await api.get(`${API_RESOURCES.ASSEMBLY_PART_META}/`, {
          searchParams: {
            'filter[order]': piece.order,
            // We only need at least one record to get the root_line_item
            'page[limit]': 1,
          },
        }).json();

        if (assemblyParts?.length) {
          setAssemblyRootLineItem(assemblyParts[0].root_line_item);
        }
      }

      if (!piece.current_print) {
        setRun(null);
        setRunUUID(null);
        return;
      }

      const currentPrint = await api.get(piece.current_print, { prefixUrl: false }).json()
      const run = currentPrint?.run ?? null;

      setRun(run);
      setRunUUID(run ? getUuid(run) : null);

      if (isAssembly || !piece.model) {
        setNoModelPiece(true);
        return;
      }
      if (!isPowderWorkflow) {
        const model = await api.get(`${API_RESOURCES.MODEL}/${getUuid(piece.model)}`).json();
        if (!model) {
          setError('Failed to retrieve model');
        }
        setModel(model);
      }
    } catch (error) {
      if (error.name === 'HTTPError' && error.response.status === 404) {
        setError('Failed to find piece.');
      } else {
        setError(error?.text);
      }
    }
  };

  useEffect(() => {
    void fetchInitialData();
  }, [pieceUUID]);

  if (error) {
    return (
      <>
        <Header excludeFromHistory title="Not Found" user={user} />
        <main role="main" className="text-center">
          <NotFound id={pieceUUID} text={error} />
        </main>
      </>
    );
  }

  if (!piece) {
    return (
      <>
        <Header title={piece?.name} user={user} />
        <main role="main" className="text-center">
          <Loader inline text="piece" className="text-muted" />
        </main>
      </>
    );
  }

  return (
    <>
      <Header title={`Piece Overview: ${piece?.name}`} user={user} />
      <main role="main" className="text-center">
        <PieceHeader piece={piece} runUUID={runUUID} />
        {noModelPiece ?
          <Alert name='qr-warning' variant='warning' className='text-center'>
            No image available for Assembly Pieces. Please see individual pieces for images.
          </Alert> :
          <>
            <ModelViewerModal show={showModelViewerModal} model={model} close={() => setShowModelViewerModal(false)} />
            <Thumbnail thumbnail={thumbnail} onClick={() => setShowModelViewerModal(true)} />
          </>}
        <Order orderID={getUuid(piece.order)} />
        <LineItem lineItemID={getUuid(piece.line_item)} piece={piece} setThumbnail={setThumbnail} />
        <Workflow pieceURI={piece.uri} workflowID={getUuid(piece.workflow)} />
        <PrintDetails pieceURI={piece.uri} />

        <div className="piece-overview-actions">
          <ScanButton withWrapper={false} />
          <ViewRunButton runUri={run} />
          {assemblyRootLineItem && (
            <Link to={getRouteURI(routes.lineItem, { uuid: getUuid(assemblyRootLineItem) })}>
              <button type='button' className='btn btn-outline-success btn-action ml-4'>
                <FontAwesomeIcon icon={faArrowAltCircleRight} size='2x' className='d-block m-auto' />
                View Assembly Parts
              </button>
            </Link>
          )}
        </div>
      </main>
    </>
  );
};

PieceOverview.propTypes = {
  user: userPropType.isRequired,
};
export default PieceOverview;
